import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { fetchEmployees } from "../../redux/employeeSlice";

const ApplyArreasModal = ({ isOpen, onClose, onSubmit, initialData }) => {
  const dispatch = useDispatch();
  const { employees, status: employeeStatus } = useSelector((state) => state.employees);
  const { register, handleSubmit, reset, setValue } = useForm();

  // Fetch employees for dropdown
  useEffect(() => {
    dispatch(fetchEmployees());
  }, [dispatch]);

  // Set form values when editing
  useEffect(() => {
    if (initialData) {
      Object.keys(initialData).forEach((key) => {
        setValue(key, initialData[key]);
      });
      // Map employeeName to employeeId for editing
      const employee = employees.find((emp) => emp.firstName + " " + emp.lastName === initialData.employeeName);
      if (employee) {
        setValue("employeeId", employee.id);
      }
    } else {
      reset();
      setValue("sessionName", "March-2024"); // Default sessionName
    }
  }, [initialData, employees, setValue, reset]);

  const handleFormSubmit = (data) => {
    // Map employeeId to employeeName and other fields for backend
    const employee = employees.find((emp) => emp.id === parseInt(data.employeeId));
    const payload = {
      employeeId: parseInt(data.employeeId),
      employeeCode: employee?.cnic || "SAS-015", // Fallback to a default code
      employeeName: employee ? `${employee.firstName} ${employee.lastName}` : data.employeeName,
      applyDate: data.applyDate,
      salary: parseFloat(data.salary),
      sessionName: data.sessionName,
      duesAmount: parseFloat(data.duesAmount),
      description: data.description || undefined,
      department: employee?.department || "Unknown",
    };
    onSubmit(payload);
    reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-medium">
            {initialData ? "Edit Arreas for Employee" : "Apply Arreas to Employee"}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium">Session Name</label>
            <input
              type="text"
              className="mt-1 w-full border border-gray-300 rounded px-3 py-2"
              {...register("sessionName")}
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Apply Date *</label>
            <input
              type="date"
              className="mt-1 w-full border border-gray-300 rounded px-3 py-2"
              {...register("applyDate", { required: true })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Salary *</label>
            <input
              type="number"
              step="0.01"
              className="mt-1 w-full border border-gray-300 rounded px-3 py-2"
              {...register("salary", { required: true })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Employee *</label>
            <select
              className="mt-1 w-full border border-gray-300 rounded px-3 py-2"
              {...register("employeeId", { required: true })}
            >
              <option value="" disabled>
                Select option
              </option>
              {employeeStatus === "loading" && <option disabled>Loading...</option>}
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.firstName} {employee.lastName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium">Amount *</label>
            <input
              type="number"
              step="0.01"
              className="mt-1 w-full border border-gray-300 rounded px-3 py-2"
              {...register("duesAmount", { required: true })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Description</label>
            <textarea
              className="mt-1 w-full border border-gray-300 rounded px-3 py-2"
              {...register("description")}
              placeholder="Enter description"
            ></textarea>
          </div>
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-[#231763] text-white px-4 py-2 rounded"
            >
              {initialData ? "Update Arreas" : "Apply Arreas"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApplyArreasModal;